// Demo chart of accounts and journals. Follows the database: codes and classes
// never change, control accounts keep their names and stay on, nothing with a
// balance or a draft line is switched off, journals must balance, posted
// journals are never edited, only reversed (once).
import { addMoney, compareMoney, isZero, negate } from '@elysian/ui';
import { bankAccountInUse } from './mockBank';
import { demoStore, TRIAL_BALANCE } from './mockDocuments';
import type { Account, Journal, JournalInput, LedgerSource } from './source';

type LedgerPart = Pick<LedgerSource, 'accounts' | 'createAccount' | 'updateAccount' | 'journals' | 'createJournal' | 'reverseJournal'>;

const delay = <T,>(v: T, ms = 80): Promise<T> => new Promise((r) => setTimeout(() => r(structuredClone(v)), ms));
const fail = (detail: string) => Promise.reject(new Error(detail));

type Line = Journal['lines'][number];
let journals: Journal[] = [];
let seq = 0;
let lastNo = 417;
/** Journal numbering (Settings → Organisation, #13); seeded journals are J-0418 onwards. */
const journalNumbering = { prefix: 'J-', next_value: 418, digits: 4 };
export const demoJournalNumbering = () => journalNumbering;
export const demoJournalNumbersUsed = (prefix: string) =>
  journals.filter((j) => j.journal_no.startsWith(prefix) && /^\d{1,18}$/.test(j.journal_no.slice(prefix.length)))
    .map((j) => Number(j.journal_no.slice(prefix.length)));
function takeJournalNumber() {
  const c = journalNumbering;
  const no = `${c.prefix}${String(c.next_value).padStart(c.digits, '0')}`;
  c.next_value += 1;
  return no;
}
const LOCKED_UNTIL = '2026-06-30'; // months covered by filed VAT returns

const acc = (code: string) => demoStore.accounts.find((a) => a.code === code);

function line(account_code: string, amount: string, description?: string, contact_id?: string): Line {
  return { id: `jl${++seq}`, account_code, amount, description, contact_id, account_name: acc(account_code)?.name ?? '' };
}

function seed() {
  seq = 0;
  lastNo = 417;
  const j = (date: string, source: Journal['source'], description: string, lines: Line[], extra: Partial<Journal> = {}): Journal =>
    ({ id: `j${++lastNo}`, journal_no: `J-${String(lastNo).padStart(4, '0')}`, date, source, description, created_by: 'ledger:user:brian', reverses: null, reversed_by: null, lines, ...extra });
  Object.assign(journalNumbering, { prefix: 'J-', digits: 4 });
  journals = [
    j('2026-09-30', 'manual', 'Accrue September subcontractor work not yet billed', [line('5100', '2400.00', 'Panel Subcontracting — September'), line('2109', '-2400.00')]),
    j('2026-09-30', 'manual', 'Depreciation, July to September: vans and equipment', [line('7800', '1250.00'), line('0031', '-1250.00')]),
    j('2026-10-01', 'reversal', 'Reversal of J-0418: Accrue September subcontractor work not yet billed', [line('2109', '2400.00'), line('5100', '-2400.00')], { reverses: 'j418', reverses_no: 'J-0418' }),
    j('2026-10-02', 'sales_invoice', 'INV-1047 Swords Pavilions', [line('1100', '4154.75', undefined, 'c1'), line('4010', '-3200.00'), line('4000', '-425.00'), line('2200', '-529.75')],
      { document_id: 'd1', document_type: 'sales_invoice', document_no: 'INV-1047' }),
    j('2026-10-06', 'receipt', 'Receipt from Parkwest Business Campus', [line('1200', '1000.00'), line('1100', '-1000.00', undefined, 'c2')],
      { document_type: 'receipt', document_no: 'RCT-0031' }),
    j('2026-10-08', 'bank_transfer', 'Transfer to deposit', [line('1210', '500.00'), line('1200', '-500.00')]),
  ];
  journalNumbering.next_value = lastNo + 1;
  journals[0]!.reversed_by = 'j420';
  journals[0]!.reversed_by_no = 'J-0420';
}
seed();
const ORIGINAL_ACCOUNTS = structuredClone(demoStore.accounts);

export function allJournals(): Journal[] {
  return journals;
}

export function journalById(id: string): Journal | undefined {
  return journals.find((j) => j.id === id);
}

/** Tests start from the template chart and the seeded journals each time. */
export function resetDemoLedger() {
  demoStore.accounts.splice(0, demoStore.accounts.length, ...structuredClone(ORIGINAL_ACCOUNTS));
  seed();
}

/** Balance per account: the year-to-date figures plus anything journalled in the demo since. */
function balanceOf(code: string): string {
  const tb = TRIAL_BALANCE.find((l) => l.code === code);
  const base = tb ? tb.balance : '0';
  const extra = journals.filter((j) => j.created_by === 'demo:session').flatMap((j) => j.lines).filter((l) => l.account_code === code).map((l) => l.amount);
  return addMoney(base, ...extra);
}

function withBalance(a: Account): Account {
  return { ...a, balance: balanceOf(a.code) };
}

export const demoLedger: LedgerPart = {
  accounts: (cls, includeInactive = false) =>
    delay(demoStore.accounts.filter((a) => (!cls || a.class === cls) && (includeInactive || a.is_active)).sort((a, b) => a.code.localeCompare(b.code)).map(withBalance)),
  createAccount: (input) => {
    if (!/^\d{3,6}$/.test(input.code)) return fail('Account codes are 3 to 6 digits.');
    if (acc(input.code)) return fail(`Account ${input.code} already exists.`);
    if (!input.name.trim()) return fail('Give the account a name.');
    const a: Account = { id: `a${input.code}`, code: input.code, name: input.name.trim(), class: input.class, control: 'none', is_active: true, default_tax_code: input.default_tax_code ?? null };
    demoStore.accounts.push(a);
    return delay(withBalance(a));
  },
  updateAccount: (code, patch) => {
    const a = acc(code);
    if (!a) return fail(`Account ${code} doesn’t exist.`);
    if (patch.name !== undefined) {
      if (!patch.name.trim()) return fail('Give the account a name.');
      if (a.control !== 'none' && a.control !== 'bank' && patch.name.trim() !== a.name) return fail(`${code} is a control account; its name stays as it is.`);
    }
    if (patch.is_active === false && a.is_active && a.control === 'bank') {
      const why = bankAccountInUse(code);
      if (why) return fail(`${a.name} can’t be made inactive: ${why}`);
    } else if (patch.is_active === false && a.is_active) {
      if (a.control !== 'none') return fail(`${code} is a control account and can’t be switched off.`);
      const bal = balanceOf(code);
      if (!isZero(bal)) return fail(`${code} still has a balance of ${bal.replace(/(\.\d\d)00$/, '$1')}; move it to another account with a journal first.`);
      const drafts = demoStore.all().filter((d) => d.status === 'draft' && d.lines.some((l) => l.account_code === code)).length;
      if (drafts) return fail(`${code} is used on ${drafts} draft document line(s).`);
    }
    if (patch.name !== undefined) a.name = patch.name.trim();
    if (patch.default_tax_code !== undefined) a.default_tax_code = patch.default_tax_code;
    if (patch.is_active !== undefined) a.is_active = patch.is_active;
    return delay(withBalance(a));
  },
  journals: (q) => delay(journals
    .filter((j) => !q.source || j.source === q.source)
    .filter((j) => !q.account || j.lines.some((l) => l.account_code === q.account))
    .filter((j) => (!q.from || j.date >= q.from) && (!q.to || j.date <= q.to))
    .sort((a, b) => b.date.localeCompare(a.date) || b.journal_no.localeCompare(a.journal_no))),
  createJournal: (input: JournalInput) => {
    if (!input.description.trim()) return fail('Describe what the journal is for.');
    if (input.lines.length < 2) return fail('A journal needs at least two lines.');
    if (input.date <= LOCKED_UNTIL) return fail(`${input.date} is in a locked period (VAT return filed). Date the journal after ${LOCKED_UNTIL}.`);
    for (const [i, l] of input.lines.entries()) {
      const a = acc(l.account_code);
      if (!a || !a.is_active) return fail(`Line ${i + 1}: account ${l.account_code} is not available.`);
      if ((a.control === 'debtors' || a.control === 'creditors') && !l.contact_id) return fail(`Lines on control account ${a.code} need a contact.`);
      if (isZero(l.amount)) return fail(`Line ${i + 1} has no amount.`);
    }
    const total = addMoney('0', ...input.lines.map((l) => l.amount));
    if (!isZero(total)) return fail(`Debits and credits differ by ${total.replace('-', '').replace(/(\.\d\d)00$/, '$1')}; a journal must balance.`);
    lastNo += 1;
    const j: Journal = { id: `j${lastNo}`, journal_no: takeJournalNumber(), date: input.date, source: 'manual', description: input.description.trim(),
      created_by: 'demo:session', reverses: null, reversed_by: null, lines: input.lines.map((l) => line(l.account_code, addMoney(l.amount), l.description, l.contact_id)) };
    journals = [j, ...journals];
    return delay(j);
  },
  // As ledger.reverse_manual_journal (#27): manual journals only; the others say where to correct them.
  reverseJournal: (id, date, reason) => {
    const j = journals.find((x) => x.id === id);
    if (!j) return fail('That journal no longer exists.');
    if (j.source !== 'manual') return fail(correctionAdvice(j));
    if (j.reversed_by) return fail(`${j.journal_no} has already been reversed by ${j.reversed_by_no ?? 'another journal'}.`);
    if (reason.trim().length < 3) return fail('Give a reason for reversing it');
    if (date < j.date) return fail(`The reversal can’t be dated before the journal it reverses (${j.date}).`);
    if (date <= LOCKED_UNTIL) return fail(`${date} is in a locked period. Date the reversal after ${LOCKED_UNTIL}.`);
    lastNo += 1;
    const r: Journal = { id: `j${lastNo}`, journal_no: takeJournalNumber(), date, source: 'reversal', description: `Reversal of ${j.journal_no}: ${reason.trim()}`,
      created_by: 'demo:session', reverses: j.id, reverses_no: j.journal_no, reversed_by: null,
      lines: j.lines.map((l) => line(l.account_code, negate(l.amount), l.description, l.contact_id)) };
    j.reversed_by = r.id;
    j.reversed_by_no = r.journal_no;
    journals = [r, ...journals];
    return delay(r);
  },
};

/** What ledger.reverse_manual_journal says for a journal that isn't reversed by hand. */
export function correctionAdvice(j: Pick<Journal, 'journal_no' | 'source' | 'document_no'>): string {
  const no = j.document_no ?? 'the document';
  switch (j.source) {
    case 'sales_invoice': return `${j.journal_no} came from invoice ${no}. Void the invoice, or raise a credit note against it, instead.`;
    case 'sales_credit': return `${j.journal_no} came from credit note ${no}. Void the credit note instead.`;
    case 'purchase_bill': return `${j.journal_no} came from bill ${no}. Void the bill, or enter a supplier credit note, instead.`;
    case 'purchase_credit': return `${j.journal_no} came from supplier credit note ${no}. Void it instead.`;
    case 'receipt': return `${j.journal_no} is money received (${no}). Correct it on the bank reconciliation (unmatch) or on the customer's page instead.`;
    case 'payment': return `${j.journal_no} is money paid out (${no}). Correct it on the bank reconciliation (unmatch) or on the supplier's page instead.`;
    case 'bank_transfer': return `${j.journal_no} is a transfer between bank accounts. Unmatch it on the bank reconciliation instead.`;
    case 'reversal': return `${j.journal_no} is itself a reversal. To undo it, post a new journal.`;
    case 'opening_balance': return `${j.journal_no} is an opening balance. Reverse its import batch on Import data instead.`;
    case 'fx_revaluation': return `${j.journal_no} is a month-end revaluation; it reverses itself the next day.`;
    case 'year_end': return `${j.journal_no} closes a year. Reopen the year on Year end instead.`;
    default: return `${j.journal_no} was posted automatically and can't be reversed by hand.`;
  }
}

// Debits are positive amounts, credits negative (as the ledger stores them).
export const debit = (amount: string) => (compareMoney(amount, '0') > 0 ? amount : '0');
export const credit = (amount: string) => (compareMoney(amount, '0') < 0 ? negate(amount) : '0');
